import { TranslateService } from '@ngx-translate/core';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { SmallBizStoreModel, ChargePointType, ChargePointLocation } from '../../stores/small-biz-store.model';
import { Injectable } from '@angular/core';
import { EvsbFinalInvoiceModel } from './evsb-final-invoice.model';
import { ButtonStateEnum } from '../../libs/component-lib/enums/button-state.enum';
import { environment } from '@app/env';

@Injectable()
export class EvsbFinalInvoiceMapper {
  constructor(private translateService: TranslateService) {}

  getGreenEnergySimulatorUrl() {
    return `${environment.websiteUrl}${
      environment[`simulatorB2B${this.translateService.currentLang.replace(/\b\w/g, (f) => f.toLocaleUpperCase())}`]
    }?promoCode=eMobilityB2B2000`;
  }

  getGreenEnergySaveUpToAmount() {
    return 80;
  }

  map({
    storeModel,
    cardPrice,
    minCharginStationPrice,
    charginStationPrice,
    chargingStationBreakdown,
  }: {
    storeModel: SmallBizStoreModel;
    cardPrice: number;
    charginStationPrice: number;
    minCharginStationPrice: number;
    chargingStationBreakdown: {
      typeCost: number;
      locationCost: number;
      smartOptionCost: number;
    };
  }): EvsbFinalInvoiceModel {
    const model: EvsbFinalInvoiceModel = {
      greenEnergySimulatorUrl: this.getGreenEnergySimulatorUrl(),
      greenEnergySaveUpToAmount: this.getGreenEnergySaveUpToAmount(),
      chargeCard: storeModel.chargeCardsAmount > 0,
      chargeCardPrice: cardPrice,
      chargeCardItems: [],
      chargePoint: storeModel.chargingStationConfig?.amount > 0,
      chargePointPrice: minCharginStationPrice,
      chargePointItems: [],
      canRequestFinalInvoice: false,
      finalInvoiceBtnState: ButtonStateEnum.disabled,
    };

    if (model.chargeCard) {
      model.canRequestFinalInvoice = true;
      model.chargeCardPrice = cardPrice * storeModel.chargeCardsAmount;
      model.chargeCardItems = [
        {
          label: marker('modules.evcSmallBiz.productOverview.chargeCard.entry'),
          count: storeModel.chargeCardsAmount,
          price: storeModel.chargeCardsAmount * cardPrice,
          description: marker('modules.evcSmallBiz.finalInvoice.chargeCard.description'),
          bottomLabel: `€ ${cardPrice}`,
        },
      ];
    }

    if (model.chargePoint) {
      model.canRequestFinalInvoice = true;
      model.chargePointItems = [
        {
          ...this.chargePointTypeLabels(storeModel.chargingStationConfig.type),
          count: storeModel.chargingStationConfig.amount,
          price: chargingStationBreakdown.typeCost * storeModel.chargingStationConfig.amount,
          bottomLabel: `€ ${chargingStationBreakdown.typeCost}`,
        },
        storeModel.chargingStationConfig.smart
          ? {
              label: marker('modules.evcSmallBiz.productOverview.chargePoint.smartChargeOption'),
              count: storeModel.chargingStationConfig.amount,
              price: chargingStationBreakdown.smartOptionCost * storeModel.chargingStationConfig.amount,
              description: marker('modules.evcSmallBiz.finalInvoice.chargePoint.description'),
              bottomLabel: `€ ${chargingStationBreakdown.smartOptionCost}`,
            }
          : undefined,
        {
          ...this.chargePointLocationLabels(storeModel.chargingStationConfig.location),
          count: storeModel.chargingStationConfig.amount,
          price: chargingStationBreakdown.locationCost * storeModel.chargingStationConfig.amount,
          bottomLabel: `€ ${chargingStationBreakdown.locationCost}`,
        },
      ].filter((i) => i !== undefined);

      model.chargePointPrice = charginStationPrice;
    }

    if (model.canRequestFinalInvoice) {
      model.finalInvoiceBtnState = ButtonStateEnum.enabled;
    }

    return model;
  }

  private chargePointTypeLabels(type: ChargePointType): { label: string; description: string } {
    switch (type) {
      case ChargePointType.connect30:
        return {
          label: marker('modules.evcSmallBiz.productOverview.chargePoint.connect30'),
          description: marker('modules.evcSmallBiz.finalInvoice.chargePoint.connect30.description'),
        };
      case ChargePointType.connect30Pro:
        return {
          label: marker('modules.evcSmallBiz.productOverview.chargePoint.connect30Pro'),
          description: marker('modules.evcSmallBiz.finalInvoice.chargePoint.connect30Pro.description'),
        };
      case ChargePointType.connect50:
        return {
          label: marker('modules.evcSmallBiz.productOverview.chargePoint.connect50'),
          description: marker('modules.evcSmallBiz.finalInvoice.chargePoint.connect50.description'),
        };
      case ChargePointType.connect50Pro:
        return {
          label: marker('modules.evcSmallBiz.productOverview.chargePoint.connect50Pro'),
          description: marker('modules.evcSmallBiz.finalInvoice.chargePoint.connect50Pro.description'),
        };
    }
  }
  private chargePointLocationLabels(type: ChargePointLocation): {
    label: string;
    description: string;
  } {
    switch (type) {
      case ChargePointLocation.poleOutside30:
      case ChargePointLocation.poleOutside50:
        return {
          label: marker('modules.evcSmallBiz.productOverview.chargePoint.poleOutside'),
          description: marker('modules.evcSmallBiz.finalInvoice.chargePoint.poleOutside.description'),
        };
      case ChargePointLocation.wallInside30:
      case ChargePointLocation.wallInside50:
        return {
          label: marker('modules.evcSmallBiz.productOverview.chargePoint.wallInside'),
          description: marker('modules.evcSmallBiz.finalInvoice.chargePoint.wallInside.description'),
        };
      case ChargePointLocation.wallOutside30:
      case ChargePointLocation.wallOutside50:
        return {
          label: marker('modules.evcSmallBiz.productOverview.chargePoint.wallOutside'),
          description: marker('modules.evcSmallBiz.finalInvoice.chargePoint.wallOutside.description'),
        };
    }
  }
}
